import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ContactSheet from "@/components/ContactSheet";
import { trackClick } from "@/lib/analytics";
import beanMustache from "@/assets/bean-mustache.svg";
import iconWhite from "@/assets/icon-white.svg";
import iconGreen from "@/assets/icon-green.svg";
import { useTheme } from "@/components/ThemeProvider";
import { clearConsent } from "@/lib/cookies";

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/madigreen72/" },
  { label: "Instagram", href: "https://www.instagram.com/keish.studio/" },
  { label: "Pinterest", href: "https://www.pinterest.com/keishstudio/" },
  { label: "Contra", href: "https://contra.com/keishstudio" },
];

const taglines = [
  "Designed by KEISH Design Studio ⋆˚꩜｡ (duh)",
];

const supportCauses = [
  { emoji: "✊🏿", label: "Black Lives Matter" },
  { emoji: "🏳️‍🌈", label: "LGBTQ+ Rights" },
  { emoji: "🏳️‍⚧️", label: "Trans Rights" },
  { emoji: "🌱", label: "Non-Profits" },
  { emoji: "📚", label: "Teachers & Educators" },
  { emoji: "♿", label: "Accessibility" },
  // { emoji: "🌍", label: "Climate Action" }, // temporarily hidden
];

const FooterSection = () => {
  const [tagline, setTagline] = useState(taglines[0]);
  const { mode } = useTheme();
  const isDark = mode === "dark";

  useEffect(() => {
    setTagline(taglines[Math.floor(Math.random() * taglines.length)]);
  }, []);

  return (
    <footer className="section-dark">
      {/* Hero CTA */}
      <div className="px-5 md:px-10 lg:px-14 pt-16 md:pt-36 pb-10 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <ContactSheet>
            <button className="group text-left w-full">
              <h2 className="font-display italic text-[clamp(3rem,10vw,6rem)] leading-[0.9] tracking-[-0.03em] text-foreground/90 group-hover:text-foreground transition-colors duration-500">
                let's make
                <br />
                something good
                <span className="inline-block ml-3 md:ml-5 w-[0.6em] h-[2px] bg-foreground/30 group-hover:w-[1.2em] group-hover:bg-foreground/70 transition-all duration-700 align-middle" />
              </h2>
            </button>
          </ContactSheet>

          <img
            src={beanMustache}
            alt=""
            className="absolute -right-2 md:right-4 bottom-0 w-16 md:w-20 opacity-10 select-none"
            style={{ animation: "spin 25s linear infinite" }}
          />
        </motion.div>
      </div>

      {/* ─── Main footer grid ─── */}
      <div className="border-t border-foreground/10">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left half — brand + nav */}
          <div className="px-5 md:px-10 lg:px-14 py-10 lg:py-20 lg:border-r border-foreground/10">
            {/* Brand */}
            <div className="mb-8 md:mb-12">
              <img src={isDark ? iconWhite : iconGreen} alt="KEISH Design Studio" className="w-20 md:w-28 lg:w-36 mb-4 md:mb-6" />
              <p className="font-body text-[11px] md:text-ts-sm text-muted-foreground/60 max-w-[320px] leading-relaxed">
                UX-focused web design for creatives &amp; ambitious founders. Based in Dallas, TX.
              </p>
            </div>

            {/* Nav columns */}
            <div className="grid grid-cols-2 gap-x-6 md:gap-x-8 gap-y-6 md:gap-y-10">
              <div className="flex flex-col gap-2 md:gap-3">
                <span className="font-body text-[10px] md:text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-1 md:mb-2">
                  Wander
                </span>
                {[
                  { label: "Work", href: "/work" },
                  { label: "About", href: "/#about" },
                  { label: "Services", href: "/#services" },
                  { label: "Shop", href: "/shop" },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="font-body text-[11px] md:text-ts-xs tracking-[0.04em] text-muted-foreground/80 hover:text-foreground transition-colors duration-300 w-fit"
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              <div className="flex flex-col gap-2 md:gap-3">
                <span className="font-body text-[10px] md:text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-1 md:mb-2">
                  Say hi
                </span>
                <a
                  href="mailto:madi@keish.studio"
                  className="font-body text-[11px] md:text-ts-xs tracking-[0.04em] text-muted-foreground/80 hover:text-foreground transition-colors duration-300 w-fit"
                >
                  madi@keish.studio
                </a>
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick("social", link.label.toLowerCase())}
                    className="font-body text-[11px] md:text-ts-xs tracking-[0.04em] text-muted-foreground/80 hover:text-foreground transition-colors duration-300 w-fit"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right half — Spotify */}
          <div className="px-5 md:px-10 lg:px-14 py-10 lg:py-20 flex flex-col pt-0">
            <span className="font-body text-[10px] md:text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-3 md:mb-4">
              What we're listening to
            </span>
            <div className="flex-1 min-h-0">
              <iframe
                title="KEISH Studio Playlist"
                src="https://open.spotify.com/embed/playlist/2fRmMlHfyvBh7Cz3eHxq1a?utm_source=generator&theme=0"
                width="100%"
                height="100%"
                style={{ borderRadius: "12px", minHeight: "352px" }}
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Instagram strip ─── */}
      <a
        href="https://www.instagram.com/keish.studio/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackClick("social", "instagram-footer")}
        className="group block border-t border-foreground/10 bg-foreground/[0.03] hover:bg-foreground/[0.06] transition-colors duration-500"
      >
        <div className="px-5 md:px-10 lg:px-14 py-4 md:py-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-base md:text-xl">📸</span>
            <span className="font-body text-ts-2xs md:text-ts-xs tracking-[0.12em] uppercase text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
              Follow us on the 'gram
            </span>
          </div>
          <span className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/50 group-hover:text-muted-foreground transition-colors duration-300 shrink-0">
            @keish.studio →
          </span>
        </div>
      </a>

      {/* ─── We proudly support ─── */}
      <div className="px-5 md:px-10 lg:px-14 py-5 md:py-8 border-t border-foreground/10">
        <span className="font-body text-[10px] md:text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground/50 mb-3 block">
          We proudly support
        </span>
        <div className="flex flex-nowrap overflow-x-auto gap-1.5 md:gap-2 md:flex-wrap md:overflow-x-visible scrollbar-hide pb-2 md:pb-0 -mx-5 px-5 md:mx-0 md:px-0">
          {supportCauses.map((cause) => (
            <span
              key={cause.label}
              className="inline-flex items-center gap-1 md:gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-foreground/[0.08] bg-foreground/[0.02] font-body text-[10px] md:text-ts-2xs text-muted-foreground/70 whitespace-nowrap shrink-0"
            >
              <span aria-hidden="true">{cause.emoji}</span>
              {cause.label}
            </span>
          ))}
        </div>
      </div>

      {/* ─── Credits bar ─── */}
      <div className="px-5 md:px-10 lg:px-14 py-6 border-t border-foreground/[0.05]">
        <div className="flex flex-col gap-4">
          {/* Legal links */}
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
            <a href="/privacy" className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">Privacy</a>
            <a href="/terms" className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">Terms</a>
            <a href="/cookies" className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">Cookies</a>
            <button
              onClick={() => {
                clearConsent();
                window.dispatchEvent(new Event("show-cookie-banner"));
              }}
              className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              Cookie Settings
            </button>
          </div>

          {/* Copyright + tagline */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <span className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/40">
              © {new Date().getFullYear()} KEISH Design Studio
            </span>
            <span className="font-body text-[10px] md:text-ts-2xs text-muted-foreground/30 tracking-[0.05em]">
              {tagline}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
